import { useEffect, useMemo, useState } from 'react';
import { ListChecks, Search, X, Plus, Loader2, Sparkles, Tags } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useListStore } from '../store/listStore';
import { favoritesApi } from '../lib/api';
import { ListCard, FavoritesPinnedCard, NewListModal } from '../components/lists';
import { SlideOver } from '../components/common/SlideOver';
import { TagManager } from '../components/tags';
import type { List } from '../types/list';

type Scope = 'mine' | 'shared' | 'team';

const SCOPES: { value: Scope; label: string }[] = [
  { value: 'mine', label: 'Mine' },
  { value: 'shared', label: 'Shared' },
  { value: 'team', label: 'Team' },
];

export default function Lists() {
  const scope = useListStore((s) => s.scope);
  const lists = useListStore((s) => s.lists);
  const isLoadingList = useListStore((s) => s.isLoadingList);
  const listError = useListStore((s) => s.listError);
  const setScope = useListStore((s) => s.setScope);
  const fetchLists = useListStore((s) => s.fetchLists);

  const navigate = useNavigate();
  const [search, setSearch] = useState('');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isTagManagerOpen, setIsTagManagerOpen] = useState(false);
  const [favorites, setFavorites] = useState<List | null>(null);

  // Initial load.
  useEffect(() => {
    fetchLists();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Favorites summary — only meaningful in the Mine scope (the backend returns
  // the system Favorites list inside `mine`). Fetching it separately guarantees
  // the list exists and gives us its count for the pinned card.
  useEffect(() => {
    if (scope !== 'mine') {
      setFavorites(null);
      return;
    }
    let cancelled = false;
    favoritesApi
      .get()
      .then(({ list }) => {
        if (!cancelled) setFavorites(list);
      })
      .catch(() => {
        if (!cancelled) setFavorites(null);
      });
    return () => {
      cancelled = true;
    };
  }, [scope]);

  // The `mine` query already includes the system Favorites list (ordered isSystem
  // first). Hide it from the grid so it doesn't render twice — it's shown as the
  // pinned FavoritesPinnedCard instead.
  const nonSystemLists = useMemo(() => lists.filter((l) => !l.isSystem), [lists]);

  const filteredLists = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return nonSystemLists;
    return nonSystemLists.filter((l) => l.name.toLowerCase().includes(q));
  }, [nonSystemLists, search]);

  const showFavorites = scope === 'mine' && favorites !== null;
  const totalCount = nonSystemLists.length;

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <div className="space-y-6">
        {/* Page header */}
        <div className="flex items-center justify-between gap-3">
          <div className="flex items-center gap-3 min-w-0">
            <div className="p-2 bg-lime-100 dark:bg-lime-900/30 rounded-lg flex-shrink-0">
              <ListChecks className="h-6 w-6 text-lime-700 dark:text-lime-400" />
            </div>
            <div className="min-w-0">
              <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">Lists</h1>
              <p className="text-sm text-gray-500 dark:text-gray-400">
                {totalCount} {totalCount === 1 ? 'list' : 'lists'}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2 flex-shrink-0">
            <button
              type="button"
              onClick={() => navigate('/explore', { state: { intent: 'standalone' } })}
              className="btn-secondary"
            >
              <Sparkles className="h-4 w-4 mr-1.5" />
              <span className="hidden sm:inline">From search</span>
              <span className="sm:hidden">Smart</span>
            </button>
            <button
              type="button"
              onClick={() => setIsTagManagerOpen(true)}
              className="btn-secondary"
            >
              <Tags className="h-4 w-4 mr-1.5" />
              <span className="hidden sm:inline">Manage tags</span>
              <span className="sm:hidden">Tags</span>
            </button>
            <button
              type="button"
              onClick={() => setIsModalOpen(true)}
              className="btn-primary"
            >
              <Plus className="h-4 w-4 mr-1.5" />
              New list
            </button>
          </div>
        </div>

        {/* Error banner */}
        {listError && (
          <div className="bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-800 rounded-lg p-4">
            <span className="text-red-800 dark:text-red-300">{listError}</span>
          </div>
        )}

        {/* Scope segmented control */}
        <div className="flex items-center gap-3" role="group" aria-label="List scope">
          <div className="inline-flex rounded-full bg-gray-100 dark:bg-gray-700 p-1">
            {SCOPES.map((opt) => {
              const isActive = scope === opt.value;
              return (
                <button
                  key={opt.value}
                  type="button"
                  onClick={() => setScope(opt.value)}
                  aria-pressed={isActive}
                  className={`px-4 py-1.5 rounded-full text-sm transition-colors ${
                    isActive
                      ? 'bg-[#b7ee41] text-gray-900 font-semibold'
                      : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200'
                  }`}
                >
                  {opt.label}
                </button>
              );
            })}
          </div>
        </div>

        {/* Search */}
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
          <input
            type="text"
            placeholder="Search lists…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="input pl-10 w-full"
          />
          {search && (
            <button
              onClick={() => setSearch('')}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
              aria-label="Clear search"
            >
              <X className="h-4 w-4" />
            </button>
          )}
        </div>

        {/* Body */}
        {isLoadingList ? (
          <div className="flex justify-center py-12">
            <Loader2 className="h-8 w-8 animate-spin text-lime-600 dark:text-lime-400" />
          </div>
        ) : filteredLists.length === 0 && !showFavorites ? (
          <div className="text-center py-12 text-gray-500 dark:text-gray-400">
            <ListChecks className="h-12 w-12 mx-auto mb-3 opacity-50" />
            {search ? (
              <>
                <p className="text-lg font-medium">No lists match “{search}”</p>
                <button
                  type="button"
                  onClick={() => setSearch('')}
                  className="mt-4 text-lime-700 dark:text-lime-400 hover:underline text-sm font-medium"
                >
                  Clear search
                </button>
              </>
            ) : (
              <>
                <p className="text-lg font-medium">No lists yet</p>
                <p className="text-sm mt-1">Create a list to group schools, programs, and people.</p>
                <button
                  type="button"
                  onClick={() => setIsModalOpen(true)}
                  className="btn-secondary text-sm mt-4"
                >
                  New list
                </button>
              </>
            )}
          </div>
        ) : (
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
            {showFavorites && favorites && <FavoritesPinnedCard list={favorites} />}
            {filteredLists.map((list) => (
              <ListCard key={list.id} list={list} />
            ))}
          </div>
        )}
      </div>

      <NewListModal isOpen={isModalOpen} onClose={() => setIsModalOpen(false)} />

      <SlideOver
        isOpen={isTagManagerOpen}
        onClose={() => setIsTagManagerOpen(false)}
        title="Manage tags"
      >
        <TagManager />
      </SlideOver>
    </div>
  );
}
